import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Container,
  Avatar
} from '@mui/material';
import {
  Biotech as BiotechIcon,
  Pets as PetsIcon,
  ShoppingCart as CartIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import FigmaNavigation from '../components/FigmaNavigation';
import Footer from '../components/shared/Footer';

const ShopLanding = () => {
  const navigate = useNavigate();

  const handleViewProduct = (productType) => {
    navigate(`/products/${productType}`);
  };

  const ProductCard = ({ product }) => {
    const isEquine = product.type === 'equine';
    const primaryColor = isEquine ? '#5c725c' : '#0d9488';
    const lightColor = isEquine ? '#e3e8e3' : '#ccfbf1';
    
    return (
      <Card sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        border: `1px solid ${lightColor}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${primaryColor}15`,
          transition: 'all 0.3s ease'
        }
      }}>
        <CardContent sx={{ flexGrow: 1, p: 4, textAlign: 'center' }}>
          <Avatar sx={{ 
            mx: 'auto',
            mb: 3, 
            bgcolor: lightColor,
            width: 80,
            height: 80,
            border: `3px solid ${primaryColor}30`
          }}>
            {isEquine ? 
              <PetsIcon sx={{ color: primaryColor, fontSize: 40 }} /> :
              <BiotechIcon sx={{ color: primaryColor, fontSize: 40 }} />
            }
          </Avatar>
          
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
            {product.name}
          </Typography>
          
          <Typography variant="h6" sx={{ color: primaryColor, fontWeight: 600, mb: 2 }}>
            {product.category}
          </Typography>
          
          <Typography variant="body1" sx={{ color: '#64748b', mb: 3, lineHeight: 1.6 }}>
            {product.description}
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ color: '#9ca3af', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
              Starting From
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
              ${product.price}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<InfoIcon />}
              onClick={() => handleViewProduct(product.type)}
              sx={{ 
                flex: 1,
                borderColor: primaryColor,
                color: primaryColor,
                '&:hover': {
                  borderColor: primaryColor,
                  bgcolor: `${primaryColor}08`
                }
              }}
            >
              Learn More
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<CartIcon />}
              onClick={() => handleViewProduct(product.type)}
              sx={{ 
                flex: 1,
                bgcolor: primaryColor,
                '&:hover': { 
                  bgcolor: isEquine ? '#3c4a3c' : '#0f766e',
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${primaryColor}40`
                },
                fontWeight: 600
              }}
            >
              Shop Now
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const products = [
    {
      id: 1,
      name: 'Lipoderma™',
      type: 'lipoderma',
      category: 'Human Aesthetic Medicine',
      description: 'Premium adipose tissue allografts for volume restoration and facial rejuvenation. FDA 361-compliant with natural-looking results.',
      price: '650'
    },
    {
      id: 2,
      name: 'equicent™ CTM',
      type: 'equicent',
      category: 'Equine Regenerative Therapy',
      description: 'Advanced umbilical cord tissue therapy for equine ligament and joint health. Proven results for athletic performance.',
      price: '450'
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <FigmaNavigation />
      
      <Container maxWidth="xl" sx={{ flexGrow: 1, py: 6 }}>
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#0f172a', 
            mb: 2,
            background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Premium Products
          </Typography>
          <Typography variant="h5" sx={{ color: '#64748b', mb: 4, maxWidth: 600, mx: 'auto' }}>
            Explore our comprehensive range of FDA-approved regenerative medicine products for human aesthetics and equine wellness
          </Typography>
        </Box>

        {/* Products Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }} justifyContent="center">
          {products.map((product) => (
            <Grid item xs={12} md={6} lg={5} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Card sx={{ 
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', 
          border: '1px solid #cbd5e1',
          textAlign: 'center',
          p: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0f172a', mb: 2 }}>
            Need Expert Guidance?
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b', mb: 3, maxWidth: 600, mx: 'auto' }}>
            Our team of medical professionals is here to help you choose the right products for your practice and provide comprehensive training and support.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login')}
            sx={{
              bgcolor: '#0d9488',
              '&:hover': { bgcolor: '#0f766e' },
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600
            }}
          >
            Get Started Today
          </Button>
        </Card>
      </Container>
      
      <Footer />
    </Box>
  );
};

export default ShopLanding;
